// @ts-strict-ignore
import Checkbox from "@dashboard/components/Checkbox";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DashboardModal } from "@dashboard/components/Modal";
import { AddressTypeInput } from "@dashboard/customers/types";
import {
  AddressFragment,
  AddressInput,
  AddressTypeEnum,
  CountryWithCodeFragment,
  Node,
  OrderErrorFragment,
} from "@dashboard/graphql";
import useAddressValidation from "@dashboard/hooks/useAddressValidation";
import { SubmitPromise } from "@dashboard/hooks/useForm";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import { buttonMessages } from "@dashboard/intl";
import { getById, transformAddressToAddressInput } from "@dashboard/misc";
import { mapCountriesToChoices } from "@dashboard/utils/maps";
import { Divider, FormControlLabel } from "@material-ui/core";
import { Input, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";

import OrderCustomerAddressesEditForm, {
  AddressInputOptionEnum,
  OrderCustomerAddressesEditFormData,
} from "./form";
import { dialogMessages } from "./messages";
import OrderCustomerAddressEdit from "./OrderCustomerAddressEdit";
import OrderCustomerAddressesSearch from "./OrderCustomerAddressesSearch";
import {
  AddressEditDialogVariant,
  OrderCustomerAddressesEditDialogOutput,
  OrderCustomerSearchAddressState,
} from "./types";
import { getAddressEditProps, hasPreSubmitErrors, validateDefaultAddress } from "./utils";

export interface OrderCustomerAddressesEditDialogProps {
  open: boolean;
  variant: AddressEditDialogVariant;
  loading: boolean;
  confirmButtonState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  orderShippingAddress?: AddressTypeInput;
  orderBillingAddress?: AddressTypeInput;
  countries?: CountryWithCodeFragment[];
  customerAddresses?: AddressFragment[];
  defaultShippingAddress?: Node;
  defaultBillingAddress?: Node;
  // Current customer tax id (VAT number / NIF) stored on the order. When
  // `onNifSubmit` is provided, an editable NIF field is shown for the billing
  // variant and persisted separately (as order metadata) on save.
  orderNif?: string;
  onNifSubmit?: (nif: string) => void;
  onClose: () => any;
  onConfirm: (data: Partial<OrderCustomerAddressesEditDialogOutput>) => SubmitPromise<any[]>;
}

const defaultSearchState: OrderCustomerSearchAddressState = {
  open: false,
  type: undefined,
};
const OrderCustomerAddressesEditDialog = (props: OrderCustomerAddressesEditDialogProps) => {
  const {
    open: defaultOpen,
    variant,
    loading,
    confirmButtonState,
    errors = [],
    countries = [],
    customerAddresses = [],
    defaultShippingAddress,
    defaultBillingAddress,
    onClose,
    onConfirm,
    orderShippingAddress,
    orderBillingAddress,
    orderNif = "",
    onNifSubmit,
  } = props;
  // The NIF belongs to the billing side: shown when editing the billing address
  // and when setting a draft's customer addresses (new or changed customer).
  const showNifField =
    !!onNifSubmit &&
    (variant === AddressEditDialogVariant.CHANGE_BILLING_ADDRESS ||
      variant === AddressEditDialogVariant.CHANGE_CUSTOMER);
  const open = !loading && defaultOpen;
  const intl = useIntl();
  const hasCustomerChanged = variant === AddressEditDialogVariant.CHANGE_CUSTOMER;
  const { errors: shippingValidationErrors, submit: handleShippingSubmit } = useAddressValidation(
    address => address,
    AddressTypeEnum.SHIPPING,
  );
  const { errors: billingValidationErrors, submit: handleBillingSubmit } = useAddressValidation(
    address => address,
    AddressTypeEnum.BILLING,
  );
  const dialogErrors = useModalDialogErrors(
    [...errors, ...shippingValidationErrors, ...billingValidationErrors],
    open,
  );
  const continueToSearchAddressesState = (data: OrderCustomerAddressesEditFormData): boolean => {
    if (hasCustomerChanged || addressSearchState.open) {
      return false;
    }

    if (!customerAddresses.length) {
      return false;
    }

    if (variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS) {
      return data.shippingAddressInputOption === AddressInputOptionEnum.CUSTOMER_ADDRESS;
    }

    return data.billingAddressInputOption === AddressInputOptionEnum.CUSTOMER_ADDRESS;
  };
  const getCustomerAddress = (selectedCustomerAddressID: string): AddressInput =>
    transformAddressToAddressInput(customerAddresses.find(getById(selectedCustomerAddressID)));
  // async because handleShippingSubmit can return a promise
  const handleAddressesSubmit = async (data: OrderCustomerAddressesEditFormData) => {
    const shippingAddress =
      customerAddresses.length > 0 &&
      data.shippingAddressInputOption === AddressInputOptionEnum.CUSTOMER_ADDRESS
        ? getCustomerAddress(data.customerShippingAddress.id)
        : await handleShippingSubmit(data.shippingAddress);
    const billingAddress =
      customerAddresses.length > 0 &&
      data.billingAddressInputOption === AddressInputOptionEnum.CUSTOMER_ADDRESS
        ? getCustomerAddress(data.customerBillingAddress.id)
        : await handleBillingSubmit(data.billingAddress);

    if (variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS) {
      return {
        shippingAddress,
        ...(data.cloneAddress && { billingAddress: shippingAddress }),
      };
    }

    if (variant === AddressEditDialogVariant.CHANGE_BILLING_ADDRESS) {
      return {
        ...(data.cloneAddress && { shippingAddress: billingAddress }),
        billingAddress,
      };
    }

    return {
      shippingAddress,
      billingAddress: data.cloneAddress ? shippingAddress : billingAddress,
    };
  };
  const getDialogTitle = (): MessageDescriptor => {
    if (addressSearchState.open) {
      if (variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS) {
        return dialogMessages.shippingTitle;
      }

      if (variant === AddressEditDialogVariant.CHANGE_BILLING_ADDRESS) {
        return dialogMessages.billingTitle;
      }
    }

    if (variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS) {
      return dialogMessages.shippingChangeTitle;
    }

    if (variant === AddressEditDialogVariant.CHANGE_BILLING_ADDRESS) {
      return dialogMessages.billingChangeTitle;
    }

    return dialogMessages.customerChangeTitle;
  };
  const getDialogDescription = (): MessageDescriptor => {
    if (customerAddresses.length === 0) {
      return dialogMessages.noAddressDescription;
    }

    if (variant === AddressEditDialogVariant.CHANGE_CUSTOMER) {
      return dialogMessages.customerChangeDescription;
    }

    return dialogMessages.addressChangeDescription;
  };
  const handleContinue = (data: OrderCustomerAddressesEditFormData) => {
    if (continueToSearchAddressesState(data)) {
      setAddressSearchState({
        open: true,
        type:
          variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS
            ? AddressTypeEnum.SHIPPING
            : AddressTypeEnum.BILLING,
      });

      return;
    }

    handleSubmit(data);
  };
  const handleSubmit = async (data: OrderCustomerAddressesEditFormData) => {
    const addressesInput = await handleAddressesSubmit(data);

    if (addressesInput && !hasPreSubmitErrors(addressesInput)) {
      await onConfirm(addressesInput as OrderCustomerAddressesEditDialogOutput);

      // Persist the VAT number (NIF) separately, as it is not part of Saleor's
      // AddressInput. Only fire when the field is shown and the value changed.
      if (showNifField && data.nif !== orderNif) {
        await onNifSubmit(data.nif);
      }

      setAddressSearchState(defaultSearchState);
    }

    return Promise.resolve([...shippingValidationErrors, ...billingValidationErrors]);
  };
  const countryChoices = mapCountriesToChoices(countries);
  const [addressSearchState, setAddressSearchState] =
    useState<OrderCustomerSearchAddressState>(defaultSearchState);
  const validatedDefaultShippingAddress = validateDefaultAddress(
    defaultShippingAddress,
    customerAddresses,
  );
  const validatedDefaultBillingAddress = validateDefaultAddress(
    defaultBillingAddress,
    customerAddresses,
  );
  const addressEditCommonProps = {
    showCard: hasCustomerChanged,
    loading,
    countryChoices,
    customerAddresses,
  };
  const exitModal = () => {
    onClose();
    setAddressSearchState(defaultSearchState);
  };

  return (
    <DashboardModal onChange={exitModal} open={open}>
      <DashboardModal.Content size="sm">
        <OrderCustomerAddressesEditForm
          countryChoices={countryChoices}
          countries={countries}
          defaultShippingAddress={validatedDefaultShippingAddress}
          defaultBillingAddress={validatedDefaultBillingAddress}
          defaultCloneAddress={hasCustomerChanged}
          initial={{
            shippingAddress: orderShippingAddress,
            billingAddress: orderBillingAddress,
            nif: orderNif,
          }}
          onSubmit={handleContinue}
        >
          {({ change, data, handlers, submit }) => {
            const shippingAddressEditProps = getAddressEditProps(
              "shipping",
              data,
              handlers,
              change,
              dialogErrors,
              setAddressSearchState,
              addressEditCommonProps,
            );
            const billingAddressEditProps = getAddressEditProps(
              "billing",
              data,
              handlers,
              change,
              dialogErrors,
              setAddressSearchState,
              addressEditCommonProps,
            );

            return (
              <DashboardModal.Grid>
                <DashboardModal.Header>
                  <FormattedMessage {...getDialogTitle()} />
                </DashboardModal.Header>

                {addressSearchState.open ? (
                  <OrderCustomerAddressesSearch
                    openFromCustomerChange={hasCustomerChanged}
                    type={addressSearchState?.type}
                    cloneAddress={data.cloneAddress}
                    formChange={change}
                    transitionState={confirmButtonState}
                    customerAddresses={customerAddresses}
                    selectedCustomerAddressId={
                      addressSearchState.type === AddressTypeEnum.SHIPPING
                        ? data.customerShippingAddress?.id
                        : data.customerBillingAddress?.id
                    }
                    onChangeCustomerShippingAddress={customerAddress =>
                      handlers.changeCustomerAddress(customerAddress, "customerShippingAddress")
                    }
                    onChangeCustomerBillingAddress={customerAddress =>
                      handlers.changeCustomerAddress(customerAddress, "customerBillingAddress")
                    }
                    exitSearch={() => setAddressSearchState(defaultSearchState)}
                  />
                ) : (
                  <>
                    <Text>
                      <FormattedMessage {...getDialogDescription()} />
                    </Text>

                    {hasCustomerChanged && (
                      <>
                        <OrderCustomerAddressEdit {...shippingAddressEditProps} />
                        <Divider />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={data.cloneAddress}
                              name="billingSameAsShipping"
                              onChange={() =>
                                change({
                                  target: {
                                    name: "cloneAddress",
                                    value: !data.cloneAddress,
                                  },
                                })
                              }
                              data-test-id="billing-same-as-shipping"
                            />
                          }
                          label={intl.formatMessage(dialogMessages.billingSameAsShipping)}
                        />
                        {!data.cloneAddress && (
                          <>
                            <Text>
                              {customerAddresses.length > 0 ? (
                                <FormattedMessage
                                  {...dialogMessages.customerChangeBillingDescription}
                                />
                              ) : (
                                <FormattedMessage {...dialogMessages.noAddressBillingDescription} />
                              )}
                            </Text>
                            <OrderCustomerAddressEdit {...billingAddressEditProps} />
                          </>
                        )}
                        {showNifField && (
                          <Input
                            data-test-id="order-customer-nif-input"
                            name="nif"
                            label={intl.formatMessage(dialogMessages.nifLabel)}
                            value={data.nif}
                            onChange={change}
                            width="100%"
                          />
                        )}
                      </>
                    )}

                    {variant === AddressEditDialogVariant.CHANGE_SHIPPING_ADDRESS && (
                      <>
                        <OrderCustomerAddressEdit {...shippingAddressEditProps} />
                        {data.shippingAddressInputOption === AddressInputOptionEnum.NEW_ADDRESS && (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={data.cloneAddress}
                                  name="billingSameAsShipping"
                                  onChange={() =>
                                    change({
                                      target: {
                                        name: "cloneAddress",
                                        value: !data.cloneAddress,
                                      },
                                    })
                                  }
                                  data-test-id="billing-same-as-shipping"
                                />
                              }
                              label={intl.formatMessage(dialogMessages.billingSameAsShipping)}
                            />
                          </>
                        )}
                      </>
                    )}

                    {variant === AddressEditDialogVariant.CHANGE_BILLING_ADDRESS && (
                      <>
                        <OrderCustomerAddressEdit {...billingAddressEditProps} />
                        {showNifField && (
                          <Input
                            data-test-id="order-billing-nif-input"
                            name="nif"
                            label={intl.formatMessage(dialogMessages.nifLabel)}
                            value={data.nif}
                            onChange={change}
                            width="100%"
                          />
                        )}
                        {data.shippingAddressInputOption === AddressInputOptionEnum.NEW_ADDRESS && (
                          <>
                            <FormSpacer />
                            <Divider />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={data.cloneAddress}
                                  name="shippingSameAsBilling"
                                  onChange={() =>
                                    change({
                                      target: {
                                        name: "cloneAddress",
                                        value: !data.cloneAddress,
                                      },
                                    })
                                  }
                                  data-test-id="billing-same-as-shipping"
                                />
                              }
                              label={intl.formatMessage(dialogMessages.shippingSameAsBilling)}
                            />
                          </>
                        )}
                      </>
                    )}

                    <DashboardModal.Actions>
                      <ConfirmButton
                        transitionState={confirmButtonState}
                        variant="primary"
                        onClick={submit}
                        data-test-id="submit"
                      >
                        <FormattedMessage
                          {...(continueToSearchAddressesState(data)
                            ? buttonMessages.continue
                            : buttonMessages.save)}
                        />
                      </ConfirmButton>
                    </DashboardModal.Actions>
                  </>
                )}
              </DashboardModal.Grid>
            );
          }}
        </OrderCustomerAddressesEditForm>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

OrderCustomerAddressesEditDialog.displayName = "OrderCustomerAddressesEditDialog";
export default OrderCustomerAddressesEditDialog;
